import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { ThreadDetailController } from './threadDetailInterface';
import { ThreadDetailViewProps } from './threadDetailProps';
import { ChatControllerBaseConfig } from '../../base';
import { MessageSectionContainerProps } from '../../section';
import { MdOutlineHeadphones } from 'react-icons/md';
import { groupMessagesByDay } from './threadDetailHelper';
import { MessagePart } from '@varos/assistant-sdk';
import { MessageItemAggregate } from '../../message';

export function createThreadDetailController(
    config: ChatControllerBaseConfig
): ThreadDetailController {
    const {
        controller: { createAction: createActionController },
    } = config;

    const callController = createActionController({
        id: 'call',
    });

    return {
        useProps(context, item, props): ThreadDetailViewProps {
            const [isScrolling, setIsScrolling] = useState(false);

            const call = callController.useProps({
                kind: 'custom',
                name: 'Call',
                onPerform() {
                    return props.onCall();
                },
            });

            // Typing indicator logic
            const [showTypingIndicator, setShowTypingIndicator] = useState(false);
            const timeoutRef = useRef<NodeJS.Timeout | null>(null);

            // Scroll tracking refs
            const messageCountRef = useRef<number>(0);
            const hasInitializedRef = useRef<boolean>(false);

            const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
                setIsScrolling(true);
                setTimeout(() => setIsScrolling(false), 1000);
            }, []);

            const sections = useMemo(() => {
                return groupMessagesByDay(item.messages).map(
                    (item): Omit<MessageSectionContainerProps, 'as'> => ({
                        item,
                    })
                );
            }, [item.messages]);

            // Typing indicator effect
            useEffect(() => {
                // Find the last message across all sections
                const lastSection = sections[sections.length - 1];
                const lastMessage: MessageItemAggregate | null =
                    lastSection?.item.messages[lastSection.item.messages.length - 1] ??
                    null;

                // Check if we should show typing indicator
                const isUserMessage = lastMessage?.message.role === 'user';
                // Check if the last message was a finished call
                const isFinishedCall = lastMessage?.message.parts.some(
                    (part) =>
                        part.type === 'tool-invocation' &&
                        part.toolInvocation.toolName === 'start_call' &&
                        part.toolInvocation.state === 'result' &&
                        part.toolInvocation.result?.status?.kind === 'finished'
                );

                // Check if message was created within the last 15 seconds
                const isRecent = lastMessage?.message.createdAt
                    ? Date.now() - lastMessage.message.createdAt.getTime() < 60000
                    : false;

                const shouldShow = (isUserMessage && isRecent) || isFinishedCall;

                if (shouldShow) {
                    // Show typing indicator after random delay
                    const delay = 400 + Math.random() * 400;
                    timeoutRef.current = setTimeout(() => {
                        setShowTypingIndicator(true);
                    }, delay);
                } else {
                    // Clear timeout and hide immediately when not needed
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                    }
                    setShowTypingIndicator(false);
                }

                // Cleanup function
                return () => {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                        timeoutRef.current = null;
                    }
                };
            }, [sections]);

            // Auto-scroll effect
            useEffect(() => {
                if (!props.messagesContainerRef?.current) return;

                const container = props.messagesContainerRef.current;
                const currentMessageCount = sections.reduce(
                    (total, section) => total + section.item.messages.length,
                    0
                );

                // Helper function to scroll to bottom with retries
                const scrollToBottom = (retries = 3, smooth = false) => {
                    const scroll = () => {
                        if (container) {
                            const beforeScroll = container.scrollHeight;

                            if (smooth) {
                                container.scrollTo({
                                    top: container.scrollHeight,
                                    behavior: 'smooth',
                                });
                            } else {
                                container.scrollTop = container.scrollHeight;
                            }

                            // If the scroll height changed after scrolling, try again
                            if (retries > 0 && beforeScroll !== container.scrollHeight) {
                                setTimeout(() => scrollToBottom(retries - 1, smooth), 50);
                            }
                        }
                    };

                    requestAnimationFrame(scroll);
                };

                // Scroll on initial load
                if (!hasInitializedRef.current) {
                    hasInitializedRef.current = true;
                    messageCountRef.current = currentMessageCount;
                    scrollToBottom();
                    return;
                }

                // Scroll only when message count increases (new messages added)
                if (currentMessageCount > messageCountRef.current) {
                    messageCountRef.current = currentMessageCount;
                    scrollToBottom(3, true); // Use smooth scrolling for new messages
                }
            }, [sections, props.messagesContainerRef]);

            // Scroll when typing indicator appears
            useEffect(() => {
                if (showTypingIndicator && props.messagesContainerRef?.current) {
                    const container = props.messagesContainerRef.current;
                    // Use the same robust scrolling approach for typing indicator
                    const scrollToBottom = () => {
                        requestAnimationFrame(() => {
                            if (container) {
                                container.scrollTop = container.scrollHeight;
                            }
                        });
                    };
                    scrollToBottom();
                }
            }, [showTypingIndicator, props.messagesContainerRef]);

            return {
                call: {
                    button: {
                        ...call.button,
                        onFocus(event) {
                            event.preventDefault();
                            event.stopPropagation();
                        },
                    },
                    tooltip: {
                        label: 'Start call',
                        openDelay: 300,
                        closeOnClick: true,
                    },
                    label: 'Start call',
                    Icon: MdOutlineHeadphones,
                },
                messages: {
                    sections: sections,
                    container: {
                        ref: props.messagesContainerRef,
                        onScroll: handleScroll,
                        isScrolling,
                    },
                },
                composer: props.composer,
                typing: {
                    isVisible: showTypingIndicator,
                    authorName: item.definition.display_name || 'Agent',
                },
            };
        },
    };
}
